import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight, Briefcase, User, Eye, EyeOff, ShieldCheck, Sparkles } from 'lucide-react';
import { bdAPI } from '../utils/api';

const BDLogin = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await bdAPI.login(formData.identifier, formData.password);
      if (response.data.success) {
        const { token, bd } = response.data;
        sessionStorage.setItem('bdToken', token.accessToken);
        if (bd) {
          sessionStorage.setItem('bdUser', JSON.stringify(bd));
        }
        navigate('/bd/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your BD credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 font-['Outfit'] overflow-hidden relative">
      {/* Dynamic Ambient Background Glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[45%] h-[45%] bg-emerald-500/20 blur-[130px] rounded-full animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[45%] h-[45%] bg-cyan-500/20 blur-[130px] rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] bg-indigo-500/10 blur-[150px] rounded-full"></div>

      <div className="w-full max-w-[460px] relative z-10">
        {/* Glass Container */}
        <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[40px] p-8 md:p-12 shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"></div>

          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-tr from-emerald-500 to-cyan-500 p-[2px] mb-5 group transition-transform hover:scale-105 duration-500 shadow-xl shadow-emerald-500/20">
               <div className="w-full h-full bg-[#0f172a] rounded-[22px] flex items-center justify-center">
                  <Briefcase size={38} className="text-emerald-400 group-hover:rotate-6 transition-transform" />
               </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-2">
              XOXO <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">BD PORTAL</span>
            </h1>
            <p className="text-slate-400 text-sm font-medium">Business Development & Agency Management</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm font-semibold text-center animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">BD Identifier</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-emerald-400 transition-colors">
                  <User size={20} />
                </div>
                <input
                  type="text"
                  name="identifier"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:bg-white/10 transition-all font-medium text-sm"
                  placeholder="BD ID (e.g. 129937), Email or Phone"
                  value={formData.identifier}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Password</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-emerald-400 transition-colors">
                  <Lock size={20} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-12 text-white placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:bg-white/10 transition-all font-medium text-sm"
                  placeholder="Enter your BD password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 shadow-xl shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Sign In to BD Portal</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          {/* Quick Help Card */}
          <div className="mt-8 pt-6 border-t border-white/5 text-center">
            <p className="text-xs text-slate-400 flex items-center justify-center gap-2">
              <ShieldCheck size={16} className="text-emerald-400" />
              <span>Dedicated portal for Business Development Managers</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BDLogin;
